import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Timeline,
    Tooltip,
    message
} from "antd";
import { CloseOutlined, DownloadOutlined, EditOutlined, EyeOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { getAccessToken } from "../../services/authApi";
import { fieldReader, fieldWriter, getLoanCustomFieldOptions } from "../../services/loanApi";
import Toast from "../shared/Toast";
import "./dealdesk.css";

const { TextArea } = Input;

const FIELD_PENDING_REQUEST = "CX.DD.PENDINGRQEUESTCOUNT";
const FIELD_IN_REVIEW_COUNT = "CX.DD.INREVIEWCOUNT";
const FIELD_REQUEST_HISTORY = "CX.DD.REQUESTHISTORY";
const FIELD_REQUEST_TYPES = "CX.DD.REQ.TYPE";

const STATUS = {
    SUBMITTED: "Submitted",
    IN_REVIEW: "InReview",
    COMPLETED: "Completed"
};

const REQUEST_TYPE_OPTIONS = [
    "Recovery Request",
    "Rush Request",
    "Cancellation Request",
    "Broker Request",
    "High Cost",
    "Program Change",
    "Exception Renegotiation",
    "CU Appraised Value Reduction",
    "Credit Re-Issue"
];

const REQUEST_CODE_PREFIX = "DD-";
const REQUEST_CODE_START = 100;
const PACIFIC_TIME_ZONE = "America/Los_Angeles";

const statusChoices = [
    { label: "Submitted", value: STATUS.SUBMITTED },
    { label: "In Review", value: STATUS.IN_REVIEW },
    { label: "Completed", value: STATUS.COMPLETED }
];

const normalizeStatus = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return STATUS.SUBMITTED;
    if (raw === "submitted") return STATUS.SUBMITTED;
    if (raw === "inreview" || raw === "in review" || raw === "pendingresolution" || raw === "pending resolution") {
        return STATUS.IN_REVIEW;
    }
    if (raw === "completed") return STATUS.COMPLETED;
    return STATUS.SUBMITTED;
};

const isPendingStatus = (status) => {
    return status === STATUS.SUBMITTED || status === STATUS.IN_REVIEW;
};

const parsePendingCount = (value) => {
    const parsed = Number.parseInt(String(value ?? "").trim(), 10);
    if (!Number.isFinite(parsed) || parsed < 0) return 0;
    return parsed;
};

const normalizeRequestType = (value) => {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
};

const parseRequestTypesFromField = (rawValue) => {
    if (!rawValue) return REQUEST_TYPE_OPTIONS;

    const normalizeParsed = (values) => {
        const cleaned = values
            .map((entry) => String(entry || "").trim())
            .filter(Boolean);
        return Array.from(new Set(cleaned));
    };

    try {
        const parsed = typeof rawValue === "string" ? JSON.parse(rawValue) : rawValue;

        if (Array.isArray(parsed)) {
            const mapped = parsed.map((entry) => {
                if (typeof entry === "string") return entry;
                return entry?.label || entry?.name || entry?.value || "";
            });
            const options = normalizeParsed(mapped);
            return options.length > 0 ? options : REQUEST_TYPE_OPTIONS;
        }
    } catch {
        // Fall back to delimited string parser when value is not JSON.
    }

    const splitValues = String(rawValue)
        .split(/\r?\n|,|;|\|/)
        .map((entry) => entry.trim());
    const options = normalizeParsed(splitValues);
    return options.length > 0 ? options : REQUEST_TYPE_OPTIONS;
};

const formatDateTime = (isoValue) => {
    if (!isoValue) return "-";
    const parsed = new Date(isoValue);
    if (Number.isNaN(parsed.getTime())) return "-";
    const base = parsed.toLocaleString("en-US", {
        timeZone: PACIFIC_TIME_ZONE
    });
    return `${base} PST`;
};

const getPacificOffset = (date) => {
    try {
        const offsetLabel = new Intl.DateTimeFormat("en-US", {
            timeZone: PACIFIC_TIME_ZONE,
            timeZoneName: "shortOffset"
        })
            .formatToParts(date)
            .find((part) => part.type === "timeZoneName")?.value;

        const match = String(offsetLabel || "").match(/(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?/i);
        if (!match) return "-08:00";

        const sign = match[1];
        const hours = String(match[2]).padStart(2, "0");
        const minutes = String(match[3] || "00").padStart(2, "0");
        return `${sign}${hours}:${minutes}`;
    } catch {
        return "-08:00";
    }
};

const getPacificTimestamp = (date = new Date()) => {
    const dateParts = new Intl.DateTimeFormat("en-US", {
        timeZone: PACIFIC_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
    }).formatToParts(date);

    const part = (type) => dateParts.find((entry) => entry.type === type)?.value || "00";
    const year = part("year");
    const month = part("month");
    const day = part("day");
    const hour = part("hour");
    const minute = part("minute");
    const second = part("second");
    const millisecond = String(date.getMilliseconds()).padStart(3, "0");
    const offset = getPacificOffset(date);

    return `${year}-${month}-${day}T${hour}:${minute}:${second}.${millisecond}${offset}`;
};

const createGuid = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }

    // Fallback for environments without randomUUID support.
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
        const random = Math.floor(Math.random() * 16);
        const value = char === "x" ? random : ((random & 0x3) | 0x8);
        return value.toString(16);
    });
};

const createEventId = () => {
    return createGuid();
};

const createRequestId = () => {
    return createGuid();
};

const extractRequestCodeNumber = (value) => {
    const match = String(value || "").trim().match(/^DD-(\d+)$/i);
    if (!match) return null;

    const parsed = Number.parseInt(match[1], 10);
    return Number.isFinite(parsed) ? parsed : null;
};

const getNextRequestCode = (requests) => {
    const highest = requests.reduce((max, request) => {
        const current = extractRequestCodeNumber(request?.requestCode);
        return current !== null && current > max ? current : max;
    }, REQUEST_CODE_START - 1);

    return `${REQUEST_CODE_PREFIX}${highest + 1}`;
};

const getUserIdentity = (user) => {
    const firstName = user?.firstName || "";
    const lastName = user?.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || user?.userName || user?.id || "Unknown User";
};

const safeParseHistory = (rawValue) => {
    if (!rawValue) return [];

    try {
        const parsed = typeof rawValue === "string" ? JSON.parse(rawValue) : rawValue;

        if (Array.isArray(parsed)) {
            return parsed.map((request) => {
                const { requestid, history, ...requestWithoutLegacyId } = request || {};
                const requestId = request?.requestId || request?.requestid || createRequestId();

                return {
                    ...requestWithoutLegacyId,
                    requestId,
                    status: normalizeStatus(request?.status),
                    history: Array.isArray(history)
                        ? history.map((historyRow) => {
                            const { eventid, ...historyWithoutLegacyId } = historyRow || {};
                            const eventId = historyRow?.eventId || historyRow?.eventid || createEventId();

                            return {
                                ...historyWithoutLegacyId,
                                eventId,
                                status: normalizeStatus(historyRow?.status),
                                previousStatus: historyRow?.previousStatus ? normalizeStatus(historyRow.previousStatus) : ""
                            };
                        })
                        : []
                };
            });
        }

        if (Array.isArray(parsed?.requests)) {
            return parsed.requests.map((request) => {
                const { requestid, history, ...requestWithoutLegacyId } = request || {};
                const requestId = request?.requestId || request?.requestid || createRequestId();

                return {
                    ...requestWithoutLegacyId,
                    requestId,
                    status: normalizeStatus(request?.status),
                    history: Array.isArray(history)
                        ? history.map((historyRow) => {
                            const { eventid, ...historyWithoutLegacyId } = historyRow || {};
                            const eventId = historyRow?.eventId || historyRow?.eventid || createEventId();

                            return {
                                ...historyWithoutLegacyId,
                                eventId,
                                status: normalizeStatus(historyRow?.status),
                                previousStatus: historyRow?.previousStatus ? normalizeStatus(historyRow.previousStatus) : ""
                            };
                        })
                        : []
                };
            });
        }

        return [];
    } catch (error) {
        console.error("Invalid deal desk request history JSON", error);
        return [];
    }
};

const buildStatusTag = (status) => {
    if (status === STATUS.SUBMITTED) return <Tag color="blue">Submitted</Tag>;
    if (status === STATUS.IN_REVIEW) return <Tag color="gold">In Review</Tag>;
    if (status === STATUS.COMPLETED) return <Tag color="green">Completed</Tag>;
    return <Tag>{status || "Unknown"}</Tag>;
};

const getStatusDisplayLabel = (status) => {
    const normalizedStatus = normalizeStatus(status);
    if (normalizedStatus === STATUS.SUBMITTED) return "Submitted";
    if (normalizedStatus === STATUS.IN_REVIEW) return "In Review";
    if (normalizedStatus === STATUS.COMPLETED) return "Completed";
    return "Unknown";
};

const getStatusBadgeClassName = (status) => {
    const normalizedStatus = normalizeStatus(status);
    if (normalizedStatus === STATUS.COMPLETED) {
        return "dd-status-pill dd-status-pill-completed";
    }
    if (normalizedStatus === STATUS.IN_REVIEW) {
        return "dd-status-pill dd-status-pill-review";
    }
    return "dd-status-pill dd-status-pill-submitted";
};

const DealDeskPage = () => {
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loanId, setLoanId] = useState("");
    const [userDisplayName, setUserDisplayName] = useState("");
    const [requests, setRequests] = useState([]);
    const [pendingRequestCount, setPendingRequestCount] = useState(0);
    const [inReviewCount, setInReviewCount] = useState(0);
    const [requestTypeOptions, setRequestTypeOptions] = useState(REQUEST_TYPE_OPTIONS);
    const [selectedRequestId, setSelectedRequestId] = useState("");
    const [detailsMode, setDetailsMode] = useState("edit");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createRequestMeta, setCreateRequestMeta] = useState({
        requestDateIso: "",
        submittedBy: ""
    });
    const [filters, setFilters] = useState({
        requestType: "All",
        status: "All"
    });
    const [activityRequestTypeFilter, setActivityRequestTypeFilter] = useState("All");
    const [summaryValidationError, setSummaryValidationError] = useState(false);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastType, setToastType] = useState("success");
    const [toastMessage, setToastMessage] = useState("");
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");

    const [draft, setDraft] = useState({
        status: STATUS.SUBMITTED,
        requestSummary: "",
        specialistNotes: "",
        completionNotes: "",
        updateComment: ""
    });

    const selectedRequest = useMemo(() => {
        return requests.find((request) => request.requestId === selectedRequestId) || null;
    }, [requests, selectedRequestId]);

    const activeRequests = useMemo(() => {
        return requests.filter((request) => request.status !== STATUS.COMPLETED);
    }, [requests]);

    const completedRequests = useMemo(() => {
        return requests.filter((request) => request.status === STATUS.COMPLETED);
    }, [requests]);

    const filteredActiveRequests = useMemo(() => {
        return activeRequests.filter((request) => {
            const typeMatch = filters.requestType === "All" || request.requestType === filters.requestType;
            const statusMatch = filters.status === "All" || request.status === filters.status;
            return typeMatch && statusMatch;
        });
    }, [activeRequests, filters]);

    const blockedRequestTypeKeys = useMemo(() => {
        return new Set(
            requests
                .filter((request) => normalizeStatus(request.status) !== STATUS.COMPLETED)
                .map((request) => normalizeRequestType(request.requestType))
        );
    }, [requests]);

    const requestTypeSelectOptions = useMemo(() => {
        return requestTypeOptions.map((type) => {
            const normalizedType = normalizeRequestType(type);
            const disabled = blockedRequestTypeKeys.has(normalizedType);

            return {
                label: disabled ? `${type} (already open)` : type,
                value: type,
                disabled
            };
        });
    }, [blockedRequestTypeKeys, requestTypeOptions]);

    const availableRequestTypes = useMemo(() => {
        return requestTypeOptions.filter((type) => !blockedRequestTypeKeys.has(normalizeRequestType(type)));
    }, [blockedRequestTypeKeys, requestTypeOptions]);

    const activityRows = useMemo(() => {
        return requests
            .flatMap((request) =>
                (request.history || []).map((entry) => ({
                    ...entry,
                    requestId: request.requestId,
                    requestCode: request.requestCode || "-",
                    requestType: request.requestType
                }))
            )
            .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
    }, [requests]);

    const filteredActivityRows = useMemo(() => {
        if (activityRequestTypeFilter === "All") return activityRows;
        return activityRows.filter((row) => row.requestType === activityRequestTypeFilter);
    }, [activityRows, activityRequestTypeFilter]);

    const activityRequestTypeOptions = useMemo(() => {
        const uniqueTypes = Array.from(new Set(requests.map((request) => request.requestType).filter(Boolean)));
        return [
            { label: "All Types", value: "All" },
            ...uniqueTypes.map((type) => ({ label: type, value: type }))
        ];
    }, [requests]);

    const showToastMsg = (msg, type = "success") => {
        setToastMessage(msg);
        setToastType(type);
        setToastVisible(true);
        setToastAnimation("animate-fade-in-right");

        setTimeout(() => {
            setToastAnimation("animate-fade-out-right");
            setTimeout(() => setToastVisible(false), 500);
        }, 5000);
    };

    const showErrorMsg = (msg) => {
        message.error(msg);
        showToastMsg(msg, "error");
    };

    const downloadCompletedRequests = () => {
        if (completedRequests.length === 0) {
            message.info("No completed requests available to export.");
            return;
        }

        const header = [
            "Request Code",
            "Request ID",
            "Request Type",
            "Status",
            "Submitted At",
            "Submitted By",
            "Completed At",
            "Completed By",
            "Request Summary",
            "Specialist Notes",
            "Completion Notes"
        ];

        const escapeCell = (value) => {
            const cell = String(value ?? "");
            if (cell.includes(",") || cell.includes("\n") || cell.includes("\"")) {
                return `"${cell.replace(/\"/g, '""')}"`;
            }
            return cell;
        };

        const rows = completedRequests.map((request) => [
            request.requestCode || "",
            request.requestId || "",
            request.requestType || "",
            request.status || "",
            request.submittedAt || "",
            request.submittedBy || "",
            request.completedAt || "",
            request.completedBy || "",
            request.requestSummary || "",
            request.specialistNotes || "",
            request.completionNotes || ""
        ]);

        const csv = [header, ...rows].map((row) => row.map(escapeCell).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `deal-desk-completed-${getPacificTimestamp().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const openEditRequest = (requestId) => {
        setDetailsMode("edit");
        setSelectedRequestId(requestId);
    };

    const openCompletedRequestView = (request) => {
        setDetailsMode("view");
        setSelectedRequestId(request?.requestId || "");
    };

    const persistRequests = async (
        nextRequests,
        token,
        currentLoanId,
        nextPendingCount = pendingRequestCount,
        nextInReviewCount = inReviewCount
    ) => {
        const resolvedPendingCount = Math.max(0, parsePendingCount(nextPendingCount));
        const resolvedInReviewCount = Math.max(0, parsePendingCount(nextInReviewCount));

        await fieldWriter(token, currentLoanId, [
            {
                id: FIELD_PENDING_REQUEST,
                value: String(resolvedPendingCount)
            },
            {
                id: FIELD_IN_REVIEW_COUNT,
                value: String(resolvedInReviewCount)
            },
            {
                id: FIELD_REQUEST_HISTORY,
                value: JSON.stringify({ requests: nextRequests })
            }
        ]);

        setPendingRequestCount(resolvedPendingCount);
        setInReviewCount(resolvedInReviewCount);
        setRequests(nextRequests);
    };

    const initializePage = async () => {
        try {
            setLoading(true);

            if (!window.elli?.script?.guest) {
                showErrorMsg("Encompass guest script is not available.");
                return;
            }

            window.elli.script.guest.create();

            const authObject = await window.elli.script.getObject("auth");
            const user = await authObject.getUser();
            setUserDisplayName(getUserIdentity(user));

            const authCode = await authObject.createAuthCode();
            const token = await getAccessToken(authCode);

            const loanObject = await window.elli.script.getObject("loan");
            const loanData = await loanObject.all();
            const currentLoanId = loanData?.loan?.id;

            if (!currentLoanId) {
                showErrorMsg("Unable to identify current loan.");
                return;
            }

            setLoanId(currentLoanId);

            const fieldData = await fieldReader(
                token,
                [FIELD_PENDING_REQUEST, FIELD_IN_REVIEW_COUNT, FIELD_REQUEST_HISTORY, FIELD_REQUEST_TYPES],
                currentLoanId
            );
            const parsedPendingCount = parsePendingCount(fieldData?.[FIELD_PENDING_REQUEST]);
            const parsedInReviewCount = parsePendingCount(fieldData?.[FIELD_IN_REVIEW_COUNT]);
            const parsedRequests = safeParseHistory(fieldData?.[FIELD_REQUEST_HISTORY]);
            let resolvedRequestTypes = [];

            try {
                resolvedRequestTypes = await getLoanCustomFieldOptions(token, FIELD_REQUEST_TYPES);
            } catch (settingsError) {
                console.warn("Unable to fetch Deal Desk request types from settings endpoint.", settingsError);
            }

            if (!Array.isArray(resolvedRequestTypes) || resolvedRequestTypes.length === 0) {
                resolvedRequestTypes = parseRequestTypesFromField(fieldData?.[FIELD_REQUEST_TYPES]);
            }

            setRequests(parsedRequests);
            setPendingRequestCount(parsedPendingCount);
            setInReviewCount(parsedInReviewCount);
            setRequestTypeOptions(resolvedRequestTypes);

            setSelectedRequestId("");
        } catch (error) {
            console.error("Failed to initialize Deal Desk page", error);
            showErrorMsg("Failed to load Deal Desk data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        initializePage();
    }, []);

    useEffect(() => {
        if (!selectedRequest) return;

        setSummaryValidationError(false);
        setDraft({
            status: normalizeStatus(selectedRequest.status),
            requestSummary: selectedRequest.requestSummary || "",
            specialistNotes: selectedRequest.specialistNotes || "",
            completionNotes: selectedRequest.completionNotes || "",
            updateComment: ""
        });
    }, [selectedRequest]);

    const handleCreateRequest = async () => {
        try {
            const values = await form.validateFields();
            const chosenTypeKey = normalizeRequestType(values.requestType);
            if (blockedRequestTypeKeys.has(chosenTypeKey)) {
                message.warning("A request of this type is already open. Complete it before adding a new one.");
                return;
            }

            setSaving(true);

            const authObject = await window.elli.script.getObject("auth");
            const authCode = await authObject.createAuthCode();
            const token = await getAccessToken(authCode);

            const now = createRequestMeta.requestDateIso || getPacificTimestamp();
            const requestId = createRequestId();
            const initialEventId = createEventId();
            const requestCode = getNextRequestCode(requests);

            const newRequest = {
                requestId,
                requestCode,
                requestType: values.requestType,
                status: STATUS.SUBMITTED,
                requestSummary: values.requestSummary,
                specialistNotes: values.specialistNotes || "",
                submittedAt: now,
                submittedBy: userDisplayName,
                lastUpdatedAt: now,
                lastUpdatedBy: userDisplayName,
                completedAt: null,
                completedBy: "",
                completionNotes: "",
                history: [
                    {
                        eventId: initialEventId,
                        action: "Submitted",
                        previousStatus: "",
                        status: STATUS.SUBMITTED,
                        occurredAt: now,
                        performedBy: userDisplayName,
                        updateComment: values.requestSummary || "Request submitted.",
                        comment: values.requestSummary || "Request submitted.",
                        requestSummary: values.requestSummary,
                        specialistNotes: values.specialistNotes || "",
                        completionNotes: ""
                    }
                ]
            };

            const nextRequests = [newRequest, ...requests];
            await persistRequests(nextRequests, token, loanId, pendingRequestCount + 1, inReviewCount);

            setDetailsMode("edit");
            setSelectedRequestId(requestId);
            setShowCreateModal(false);
            form.resetFields();
            showToastMsg("Deal Desk request created.", "success");
        } catch (error) {
            if (error?.errorFields) return;
            console.error("Failed to create Deal Desk request", error);
            showErrorMsg("Failed to create request.");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveUpdate = async () => {
        if (!selectedRequest) return;

        const summary = String(draft.requestSummary || "").trim();
        const specialistNotes = String(draft.specialistNotes || "").trim();

        if (!summary) {
            setSummaryValidationError(true);
            showErrorMsg("Request Summary is required.");
            return;
        }

        setSummaryValidationError(false);

        if (summary.length > 5000 || specialistNotes.length > 5000) {
            showErrorMsg("Request Summary and Loan Solution Specialist Notes must be 5000 characters or less.");
            return;
        }

        try {
            setSaving(true);

            const authObject = await window.elli.script.getObject("auth");
            const authCode = await authObject.createAuthCode();
            const token = await getAccessToken(authCode);

            const now = getPacificTimestamp();
            const nextStatus = normalizeStatus(draft.status);
            const previousStatus = normalizeStatus(selectedRequest.status);
            const statusChanged = nextStatus !== previousStatus;

            let action = "NotesUpdated";
            if (statusChanged) {
                action = nextStatus === STATUS.COMPLETED ? "Completed" : "StatusUpdated";
            }

            const completionNotes = nextStatus === STATUS.COMPLETED ? draft.completionNotes || "" : "";
            const updateEventId = createEventId();

            const updatedRequest = {
                ...selectedRequest,
                status: nextStatus,
                requestSummary: summary,
                specialistNotes,
                lastUpdatedAt: now,
                lastUpdatedBy: userDisplayName,
                completedAt: nextStatus === STATUS.COMPLETED ? (selectedRequest.completedAt || now) : null,
                completedBy: nextStatus === STATUS.COMPLETED ? (selectedRequest.completedBy || userDisplayName) : "",
                completionNotes,
                history: [
                    ...(selectedRequest.history || []),
                    {
                        eventId: updateEventId,
                        action,
                        previousStatus: statusChanged ? previousStatus : previousStatus,
                        status: nextStatus,
                        occurredAt: now,
                        performedBy: userDisplayName,
                        updateComment: draft.updateComment || "Request updated.",
                        comment: draft.updateComment || "Request updated.",
                        requestSummary: summary,
                        specialistNotes,
                        completionNotes
                    }
                ]
            };

            const nextRequests = requests.map((request) =>
                request.requestId === selectedRequest.requestId ? updatedRequest : request
            );

            let pendingCountDelta = 0;
            if (previousStatus !== STATUS.COMPLETED && nextStatus === STATUS.COMPLETED) {
                pendingCountDelta = -1;
            } else if (previousStatus === STATUS.COMPLETED && nextStatus !== STATUS.COMPLETED) {
                pendingCountDelta = 1;
            }

            let inReviewCountDelta = 0;
            if (previousStatus !== STATUS.IN_REVIEW && nextStatus === STATUS.IN_REVIEW) {
                inReviewCountDelta = 1;
            } else if (previousStatus === STATUS.IN_REVIEW && nextStatus !== STATUS.IN_REVIEW) {
                inReviewCountDelta = -1;
            }

            await persistRequests(
                nextRequests,
                token,
                loanId,
                pendingRequestCount + pendingCountDelta,
                inReviewCount + inReviewCountDelta
            );
            setDraft((previous) => ({ ...previous, updateComment: "" }));
            showToastMsg("Deal Desk request updated.", "success");
            setTimeout(() => {
                setSelectedRequestId("");
            }, 200);
        } catch (error) {
            console.error("Failed to save Deal Desk update", error);
            showErrorMsg("Failed to save update.");
        } finally {
            setSaving(false);
        }
    };

    const openCreateRequestModal = () => {
        if (availableRequestTypes.length === 0) {
            message.info("All request types currently have an open request. Complete one to add a new request.");
            return;
        }

        const draftDate = getPacificTimestamp();
        setCreateRequestMeta({
            requestDateIso: draftDate,
            submittedBy: userDisplayName || "-"
        });
        form.setFieldsValue({ requestType: availableRequestTypes[0] });
        setShowCreateModal(true);
    };

    const activeColumns = [
        {
            title: "Request Code",
            dataIndex: "requestCode",
            key: "requestCode",
            render: (value, record) => (
                <Button
                    type="link"
                    className="dd-link-button"
                    onClick={() => openEditRequest(record.requestId)}
                >
                    {value || "-"}
                </Button>
            )
        },
        {
            title: "Type",
            dataIndex: "requestType",
            key: "requestType"
        },
        {
            title: "Submitted",
            dataIndex: "submittedAt",
            key: "submittedAt",
            render: (value) => formatDateTime(value)
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value) => buildStatusTag(normalizeStatus(value))
        },
        {
            title: "Updated",
            dataIndex: "lastUpdatedAt",
            key: "lastUpdatedAt",
            render: (value) => formatDateTime(value)
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            render: (_, record) => (
                <Tooltip title="Open request">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => openEditRequest(record.requestId)}
                    />
                </Tooltip>
            )
        }
    ];

    const completedColumns = [
        {
            title: "Request Code",
            dataIndex: "requestCode",
            key: "requestCode",
            render: (value, record) => (
                <Button
                    type="link"
                    className="dd-link-button"
                    onClick={() => openCompletedRequestView(record)}
                >
                    {value || "-"}
                </Button>
            )
        },
        {
            title: "Type",
            dataIndex: "requestType",
            key: "requestType"
        },
        {
            title: "Completed",
            dataIndex: "completedAt",
            key: "completedAt",
            render: (value) => formatDateTime(value)
        },
        {
            title: "Completed By",
            dataIndex: "completedBy",
            key: "completedBy",
            render: (value) => value || "-"
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value) => buildStatusTag(normalizeStatus(value))
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <Button type="link" icon={<EyeOutlined />} onClick={() => openCompletedRequestView(record)}>
                    View
                </Button>
            )
        }
    ];

    const activityColumns = [
        {
            title: "Request Code",
            dataIndex: "requestCode",
            key: "requestCode"
        },
        {
            title: "Date / Time",
            dataIndex: "occurredAt",
            key: "occurredAt",
            render: (value) => formatDateTime(value)
        },
        {
            title: "Action",
            dataIndex: "action",
            key: "action"
        },
        {
            title: "Status Change",
            key: "statusChange",
            render: (_, record) => {
                const previous = record.previousStatus || "-";
                const next = record.status || "-";
                return `${previous} -> ${next}`;
            }
        },
        {
            title: "User",
            dataIndex: "performedBy",
            key: "performedBy"
        }
    ];

    if (loading) {
        return (
            <div className="dd-loading-wrap">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="dd-page-wrap">
            <Row gutter={16}>
                <Col xs={24} lg={17}>
                    <div className="dd-header">
                        <div>
                            <h1 className="dd-title">Deal Desk</h1>
                            <p className="dd-subtitle">Manage multiple requests for this loan</p>
                        </div>
                    </div>

                    <Alert
                        type="info"
                        showIcon
                        className="dd-top-alert"
                        message="Different request types can run concurrently. One open request per type is recommended."
                    />

                    <Card
                        className="dd-card"
                        title={`Active Requests (${activeRequests.length})`}
                        extra={(
                            <Space size={12} wrap>
                                <div>
                                    <span className="dd-label">Request Type</span>
                                    <Select
                                        value={filters.requestType}
                                        onChange={(value) => setFilters((previous) => ({ ...previous, requestType: value }))}
                                        options={[
                                            { label: "All Types", value: "All" },
                                            ...requestTypeOptions.map((type) => ({ label: type, value: type }))
                                        ]}
                                        style={{ width: 190 }}
                                    />
                                </div>
                                <div>
                                    <span className="dd-label">Status</span>
                                    <Select
                                        value={filters.status}
                                        onChange={(value) => setFilters((previous) => ({ ...previous, status: value }))}
                                        options={[
                                            { label: "All Statuses", value: "All" },
                                            ...statusChoices
                                        ]}
                                        style={{ width: 170 }}
                                    />
                                </div>
                            </Space>
                        )}
                    >

                        <Table
                            rowKey="requestId"
                            columns={activeColumns}
                            dataSource={filteredActiveRequests}
                            pagination={false}
                            size="small"
                            bordered
                        />
                    </Card>

                    <Card
                        className="dd-card"
                        title={`Completion History (${completedRequests.length})`}
                        extra={(
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={downloadCompletedRequests}
                                disabled={completedRequests.length === 0}
                            >
                                Export
                            </Button>
                        )}
                    >
                        <Table
                            rowKey="requestId"
                            columns={completedColumns}
                            dataSource={completedRequests}
                            pagination={false}
                            size="small"
                            bordered
                        />
                    </Card>

                    <Card
                        className="dd-card"
                        title="Request Activity"
                        extra={(
                            <Select
                                value={activityRequestTypeFilter}
                                options={activityRequestTypeOptions}
                                onChange={setActivityRequestTypeFilter}
                                style={{ width: 220 }}
                            />
                        )}
                    >
                        <Table
                            rowKey={(record) => `${record.eventId}-${record.requestId}`}
                            columns={activityColumns}
                            dataSource={filteredActivityRows}
                            pagination={false}
                            size="small"
                            bordered
                            scroll={{ y: 320 }}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={7}>
                    <div className="dd-right-actions">
                        <Space>
                            <Tag color="blue" className="dd-count-tag">{activeRequests.length} Active</Tag>
                            <Tag color="green" className="dd-count-tag">{completedRequests.length} Completed</Tag>
                            <Button icon={<ReloadOutlined />} onClick={initializePage} />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                className="dd-primary-btn"
                                onClick={openCreateRequestModal}
                                disabled={availableRequestTypes.length === 0}
                            >
                                New Request
                            </Button>
                        </Space>
                    </div>

                    <Card
                        className="dd-card dd-sticky-card dd-request-details-card"
                        title="Request Details"
                        extra={detailsMode === "view" && selectedRequest ? (
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    setSelectedRequestId("");
                                    setDetailsMode("edit");
                                }}
                            />
                        ) : null}
                    >
                        {!selectedRequest ? (
                            <div className="dd-empty-wrap">
                                <Empty description="No request selected" />
                                <p className="dd-empty-note">Select a request to view its details and history.</p>
                                <div className="dd-empty-help">
                                    <h4>How to open a request</h4>
                                    <p><EditOutlined /> Active requests: click a request code or the pencil icon.</p>
                                    <p><EyeOutlined /> Completed requests: click View to open read-only details.</p>
                                </div>
                            </div>
                        ) : (
                            <div className="dd-details-wrap">
                                <div className="dd-details-head">
                                    <div>
                                        <h3 className="dd-request-id">{selectedRequest.requestCode}</h3>
                                        <div className="dd-type-tile">
                                            <span className="dd-type-title">Type</span>
                                            <p className="dd-type-text">{selectedRequest.requestType || "-"}</p>
                                        </div>
                                    </div>
                                    <span className={getStatusBadgeClassName(detailsMode === "edit" ? draft.status : selectedRequest.status)}>
                                        {getStatusDisplayLabel(detailsMode === "edit" ? draft.status : selectedRequest.status)}
                                    </span>
                                </div>

                                <div className="dd-meta-grid">
                                    <div className="dd-meta-item">
                                        <span className="dd-label">Request Date</span>
                                        <p className="dd-meta-value">{formatDateTime(selectedRequest.submittedAt)}</p>
                                    </div>
                                    <div className="dd-meta-item">
                                        <span className="dd-label">Submitted By</span>
                                        <p className="dd-meta-value">{selectedRequest.submittedBy || "-"}</p>
                                    </div>
                                </div>

                                {detailsMode === "edit" ? (
                                    <>
                                        <div className="dd-field-block">
                                            <span className="dd-label dd-label-required">Request Summary</span>
                                            <TextArea
                                                rows={3}
                                                maxLength={5000}
                                                showCount
                                                className={summaryValidationError ? "dd-input-error" : ""}
                                                value={draft.requestSummary}
                                                onChange={(event) => {
                                                    const nextValue = event.target.value;
                                                    if (summaryValidationError && nextValue.trim()) {
                                                        setSummaryValidationError(false);
                                                    }
                                                    setDraft((previous) => ({ ...previous, requestSummary: nextValue }));
                                                }}
                                            />
                                        </div>

                                        <div className="dd-field-block">
                                            <span className="dd-label">Loan Solution Specialist Notes</span>
                                            <TextArea
                                                rows={3}
                                                maxLength={5000}
                                                showCount
                                                value={draft.specialistNotes}
                                                onChange={(event) =>
                                                    setDraft((previous) => ({ ...previous, specialistNotes: event.target.value }))
                                                }
                                            />
                                        </div>

                                        <div className="dd-field-block">
                                            <span className="dd-label">Status</span>
                                            <Select
                                                style={{ width: "100%" }}
                                                value={draft.status}
                                                options={statusChoices}
                                                onChange={(value) => setDraft((previous) => ({ ...previous, status: value }))}
                                            />
                                        </div>

                                        <div className="dd-field-block">
                                            <span className="dd-label">Update Comment</span>
                                            <TextArea
                                                rows={3}
                                                placeholder="Describe this update for the audit trail"
                                                value={draft.updateComment}
                                                onChange={(event) =>
                                                    setDraft((previous) => ({ ...previous, updateComment: event.target.value }))
                                                }
                                            />
                                        </div>

                                        <Space className="dd-details-actions">
                                            <Button
                                                type="primary"
                                                className="dd-primary-btn"
                                                loading={saving}
                                                onClick={handleSaveUpdate}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    if (!selectedRequest) return;
                                                    setSelectedRequestId("");
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </Space>
                                    </>
                                ) : (
                                    <>
                                        <div className="dd-field-block">
                                            <span className="dd-label">Status</span>
                                            <div>{buildStatusTag(normalizeStatus(selectedRequest.status))}</div>
                                        </div>

                                        <div className="dd-field-block">
                                            <span className="dd-label">Request Summary</span>
                                            <p>{selectedRequest.requestSummary || "-"}</p>
                                        </div>

                                        <div className="dd-field-block">
                                            <span className="dd-label">Loan Solution Specialist Notes</span>
                                            <p>{selectedRequest.specialistNotes || "-"}</p>
                                        </div>

                                        <div className="dd-field-block">
                                            <span className="dd-label">Completion Notes</span>
                                            <p>{selectedRequest.completionNotes || "-"}</p>
                                        </div>
                                    </>

                                )}

                                {Array.isArray(selectedRequest.history) && selectedRequest.history.length > 0 && (
                                    <div className="dd-field-block">
                                        <span className="dd-label">Request History Timeline</span>
                                        <Timeline
                                            items={[...selectedRequest.history]
                                                .sort(
                                                    (a, b) =>
                                                        new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()
                                                )
                                                .map((historyRow) => {
                                                    const actionComment = String(
                                                        historyRow.updateComment ||
                                                        historyRow.comment ||
                                                        historyRow.completionNotes ||
                                                        historyRow.requestSummary ||
                                                        "No comment"
                                                    ).trim();

                                                    return {
                                                        color:
                                                            normalizeStatus(historyRow.status) === STATUS.COMPLETED
                                                                ? "green"
                                                                : normalizeStatus(historyRow.status) === STATUS.IN_REVIEW
                                                                    ? "gold"
                                                                    : "blue",
                                                        children: (
                                                            <div>
                                                                <strong>{historyRow.action || "Updated"}</strong>
                                                                <div>{formatDateTime(historyRow.occurredAt)}</div>
                                                                <div>{historyRow.performedBy || "-"}</div>
                                                                <div><strong>Update Comment:</strong> {actionComment}</div>
                                                            </div>
                                                        )
                                                    };
                                                })}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>

            <Modal
                title="New Deal Desk Request"
                open={showCreateModal}
                onCancel={() => {
                    setShowCreateModal(false);
                    form.resetFields();
                    setCreateRequestMeta({ requestDateIso: "", submittedBy: "" });
                }}
                onOk={handleCreateRequest}
                okText="Submit Request"
                okButtonProps={{ className: "dd-primary-btn", loading: saving }}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    className="dd-modal-alert"
                    message="You can submit different request types concurrently."
                />

                <Form
                    layout="vertical"
                    form={form}
                    initialValues={{
                        requestType: requestTypeOptions[0]
                    }}
                >
                    <Form.Item
                        label="Request Type"
                        name="requestType"
                        rules={[{ required: true, message: "Please select request type" }]}
                    >
                        <Select options={requestTypeSelectOptions} />
                    </Form.Item>

                    <Form.Item
                        label="Request Summary"
                        name="requestSummary"
                        rules={[{ required: true, message: "Please enter request summary" }]}
                    >
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item label="Loan Solution Specialist Notes" name="specialistNotes">
                        <TextArea rows={3} />
                    </Form.Item>

                    <Row gutter={12}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Request Date">
                                <Input
                                    value={formatDateTime(createRequestMeta.requestDateIso)}
                                    readOnly
                                    disabled
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Submitted By">
                                <Input
                                    value={createRequestMeta.submittedBy || userDisplayName || "-"}
                                    readOnly
                                    disabled
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="dd-submission-details">
                        <h4 className="dd-submission-title">Submission details</h4>
                        <div className="dd-submission-grid">
                            <div>
                                <span className="dd-submission-label">Initial status</span>
                                <p className="dd-submission-value">Submitted</p>
                            </div>
                        </div>
                    </div>

                </Form>
            </Modal>

            <Toast
                visible={toastVisible}
                type={toastType}
                message={toastMessage}
                onClose={() => setToastVisible(false)}
                animationClass={toastAnimation}
            />

        </div>
    );
};

export default DealDeskPage;
